import type { Role } from '../types';

export type CapabilityArea =
  | 'Projects'
  | 'Evaluations'
  | 'Framework'
  | 'Users'
  | 'Capabilities'
  | 'System';

export interface Capability {
  id: string;
  label: string;
  description: string;
  roles: Role[];
  area: CapabilityArea;
}

export const ROLES_IN_ORDER: Role[] = [
  'basic-user',
  'reviewer',
  'framework-manager',
  'admin',
];

export const ROLE_LABELS: Record<Role, string> = {
  'basic-user': 'Basic user',
  reviewer: 'Reviewer',
  'framework-manager': 'Framework manager',
  admin: 'Admin',
};

export const ROLE_TAGLINES: Record<Role, string> = {
  'basic-user':
    'Submit projects and view your own submissions.',
  reviewer:
    'Evaluate projects, score rubric sections, and reach consensus with other reviewers.',
  'framework-manager':
    'Manage the evaluation framework (rubric, weights, capabilities) that guides every review.',
  admin:
    'Full access to users, framework, capabilities, and system configuration.',
};

export const CAPABILITIES: Capability[] = [
  // Projects
  {
    id: 'project.submit',
    label: 'Submit a project',
    description: 'Upload a PDF or paste text to create a new project for evaluation.',
    roles: ['basic-user', 'reviewer', 'framework-manager', 'admin'],
    area: 'Projects',
  },
  {
    id: 'project.view-own',
    label: 'View your own submissions',
    description: 'See projects you submitted and their evaluation status.',
    roles: ['basic-user', 'reviewer', 'framework-manager', 'admin'],
    area: 'Projects',
  },
  {
    id: 'project.view-all',
    label: 'View all projects',
    description: 'Browse every project in the system, not just your own.',
    roles: ['reviewer', 'framework-manager', 'admin'],
    area: 'Projects',
  },

  // Evaluations
  {
    id: 'evaluation.trigger',
    label: 'Trigger AI evaluation',
    description: 'Run Claude against a project to produce AI scores and rationales.',
    roles: ['reviewer', 'framework-manager', 'admin'],
    area: 'Evaluations',
  },
  {
    id: 'evaluation.score',
    label: 'Score rubric sections',
    description: 'Provide your own scores and comments alongside the AI evaluation.',
    roles: ['reviewer', 'framework-manager', 'admin'],
    area: 'Evaluations',
  },
  {
    id: 'evaluation.consensus',
    label: 'Reach consensus',
    description: 'Finalize the evaluation by merging AI and reviewer scores into a decision.',
    roles: ['reviewer', 'framework-manager', 'admin'],
    area: 'Evaluations',
  },

  // Framework
  {
    id: 'framework.view',
    label: 'View the framework',
    description: 'See the current rubric, weights, and scoring criteria.',
    roles: ['basic-user', 'reviewer', 'framework-manager', 'admin'],
    area: 'Framework',
  },
  {
    id: 'framework.edit',
    label: 'Edit the framework',
    description: 'Change rubric sections, weights, and scoring rules that apply to all future evaluations.',
    roles: ['framework-manager', 'admin'],
    area: 'Framework',
  },

  // Capabilities
  {
    id: 'capabilities.view',
    label: 'View capabilities',
    description: 'See the list of company capabilities used as context during evaluation.',
    roles: ['reviewer', 'framework-manager', 'admin'],
    area: 'Capabilities',
  },
  {
    id: 'capabilities.sync',
    label: 'Sync capabilities from Ragnarok',
    description: 'Pull the latest capability documents from the Ragnarok RAG service.',
    roles: ['reviewer', 'framework-manager', 'admin'],
    area: 'Capabilities',
  },

  // Users
  {
    id: 'users.manage',
    label: 'Manage users',
    description: 'Assign roles and deactivate accounts.',
    roles: ['admin'],
    area: 'Users',
  },

  // System
  {
    id: 'system.mcp',
    label: 'Use the MCP endpoint',
    description: 'Read projects and evaluations from external AI tools via the API-key-authenticated MCP server.',
    roles: ['admin'],
    area: 'System',
  },
];

export function capabilitiesForRole(role: Role): Capability[] {
  return CAPABILITIES.filter((c) => c.roles.includes(role));
}

export function roleHasCapability(role: Role, capabilityId: string): boolean {
  const cap = CAPABILITIES.find((c) => c.id === capabilityId);
  return cap ? cap.roles.includes(role) : false;
}

/**
 * Check whether `role` meets or exceeds `requiredRole` in the role hierarchy
 * (basic-user < reviewer < framework-manager < admin). Mirrors the server-side
 * `has_role_or_higher` helper in api/src/lib/auth.py so the two planes agree.
 *
 * This is advisory-only UI gating; the server remains authoritative.
 */
export function hasRoleOrHigher(
  role: Role | undefined,
  requiredRole: Role,
): boolean {
  if (!role) return false;
  return ROLES_IN_ORDER.indexOf(role) >= ROLES_IN_ORDER.indexOf(requiredRole);
}

// Self-check: run once at module load so misconfigured manifests blow up fast in dev.
(function validate() {
  const seen = new Set<string>();
  for (const cap of CAPABILITIES) {
    if (seen.has(cap.id)) {
      throw new Error(`Duplicate capability id: ${cap.id}`);
    }
    seen.add(cap.id);
    if (cap.roles.length === 0) {
      throw new Error(`Capability ${cap.id} has no roles`);
    }
  }
})();
